import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Sparkles,
  Target,
  Heart,
  MessageCircle,
  TrendingUp,
  Users,
  AlertCircle,
  RefreshCw,
  Edit,
  Save,
  CheckCircle2,
  XCircle,
  Star,
  Activity,
} from 'lucide-react';
import { sendChatMessage } from '../../../api/AI/chat';
import { getNarrativeMaterial, parseSignalsSummary, NarrativeMaterialView } from '../../../api/PreformanceManagement/PerformanceSnapshot';
import type { Employee, ReviewData } from '../index';

interface NarrativeCompilationPanelProps {
  employee: Employee;
  reviewData: ReviewData;
  /**
   * A snapshot past Draft cannot be written to (`Update` refuses it), so the panel neither offers
   * to edit nor calls the model — a generation that can never be saved is spend with no product.
   */
  readOnly?: boolean;
  /**
   * CO-441 — may this panel call the model ON MOUNT, without being asked?
   *
   * Only the page can know, because only the page knows HOW the snapshot was opened. Pressing
   * Generate for the month on screen is a deliberate act and arms it; clicking through the
   * "previous months" history is browsing, and must cost nothing. The panel remounts on every
   * change of snapshot (the page keys it on the snapshot id), so an unconditional mount-time
   * generation billed the model once per click and autosaved narratives nobody asked for into
   * other months' records — the CO-435 defect below, arriving by a new route.
   *
   * DEFAULTS TO FALSE: a caller that has not thought about it does not get to spend money.
   */
  autoGenerate?: boolean;
  onNarrativeUpdate: (narrative: NarrativeData) => void;
}

interface NarrativeData {
  summary: string;
  goalsAchieved: string[];
  goalsMissed: string[];
  impactHighlights: string[];
  feedbackThemes: string[];
  behaviorPatterns: string[];
  teamContext: string;
}

/**
 * The starting point for a narrative the MANAGER writes. Deliberately empty: pre-filling it with
 * anything derived from the score card is exactly the fabrication the old AI fallback committed.
 */
const EMPTY_NARRATIVE: NarrativeData = {
  summary: '',
  goalsAchieved: [],
  goalsMissed: [],
  impactHighlights: [],
  feedbackThemes: [],
  behaviorPatterns: [],
  teamContext: '',
};

/** Caps how much raw material goes into one prompt, so a busy quarter cannot blow the context. */
const MAX_MATERIAL_LINES = 12;

/**
 * CO-439 — render a snapshot `Period` the way a person reads it.
 *
 * `Period` stays a string, and TWO shapes must render for at least one rollback window
 * (expand/contract, CLAUDE.md §ZDD):
 *   "yyyy-MM"  — every write from CO-439 onward. Formatted as a month name in the reader's locale.
 *   "yyyy-Qn"  — every row written before CO-439. Passed through VERBATIM: a quarter row's numbers
 *                came from a rolling six months anchored to its generation date, not from the quarter
 *                it is labelled with, so naming a month for it would put a month on data that never
 *                measured one. There is no backfill and no rewrite of those rows.
 * UTC throughout — a local-time month boundary is the DST class of bug.
 */
const formatPeriodLabel = (
  period: string | null | undefined,
  language: string,
): string => {
  if (!period) return '';
  const month = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
  if (!month) return period;
  const date = new Date(Date.UTC(Number(month[1]), Number(month[2]) - 1, 1));
  try {
    return new Intl.DateTimeFormat(language, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  } catch {
    // An unknown BCP-47 tag throws rather than falling back, and a narrative must not fail over a label.
    return period;
  }
};

const bulletList = (lines: string[], emptyLabel: string): string =>
  lines.length > 0
    ? lines.slice(0, MAX_MATERIAL_LINES).map((l) => `- ${l}`).join('\n')
    : `- ${emptyLabel}`;

const NarrativeCompilationPanel = ({
  employee,
  reviewData,
  readOnly = false,
  autoGenerate = false,
  onNarrativeUpdate,
}: NarrativeCompilationPanelProps) => {
  const { t, i18n } = useTranslation();
  /**
   * CO-435 — seeded from the PAGE, which seeds it from the persisted `narrativeSummary`. A narrative
   * is generated ONCE and then belongs to the manager: every later visit reads what was stored, and
   * only an explicit Regenerate calls the model again. Before this, the panel regenerated on every
   * mount, so the manager's edit was destroyed by their own tab switch and the AI was re-billed for
   * a narrative nobody asked for.
   */
  const [narrative, setNarrative] = useState<NarrativeData | null>(
    () => (reviewData.narrative as unknown as NarrativeData | null) ?? null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNarrative, setEditedNarrative] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Regenerate discards the manager's edit, so it asks first — inline, not in a modal. */
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);

  // Parse snapshot JSON summary fields for use in prompts and fallback narrative
  const snapshot = reviewData.snapshot;
  let goalsData = { total: 0, completed: 0, onTrack: 0, atRisk: 0, overdue: 0 };
  let checkInsData = { total: 0, withContent: 0, avgQuality: 0 };
  let recognitionData = { received: 0, given: 0 };
  let strengthsData: { topSkills: { skillName: string; proficiency: string; endorsementCount: number }[] } = { topSkills: [] };

  try { if (snapshot?.goalsSummary) goalsData = JSON.parse(snapshot.goalsSummary); } catch { /* ignore parse errors */ }
  try { if (snapshot?.checkInsSummary) checkInsData = JSON.parse(snapshot.checkInsSummary); } catch { /* ignore parse errors */ }
  try { if (snapshot?.recognitionSummary) recognitionData = JSON.parse(snapshot.recognitionSummary); } catch { /* ignore parse errors */ }
  try { if (snapshot?.strengthsSummary) strengthsData = JSON.parse(snapshot.strengthsSummary); } catch { /* ignore parse errors */ }

  /**
   * Reflections come off `signalsSummary` rather than a `*Summary` column of their own - the count
   * was added to the signals JSON, and no column exists for it. `undefined` on any snapshot
   * generated before reflections were counted, which is why the prompt line below is omitted
   * entirely in that case rather than asserting a zero nobody measured.
   */
  const reflectionsData = parseSignalsSummary(snapshot?.signalsSummary)?.reflections ?? null;

  /**
   * CO-435 — generate ONCE, at first open, and only when nothing is stored yet. A snapshot that
   * already carries a narrative (generated earlier, possibly edited since) is never re-generated
   * behind the manager's back — which is also what makes a get-or-create Generate cheap to click
   * twice. A snapshot past Draft is never generated for at all: the result could not be saved.
   *
   * 🔴 CO-441 — AND ONLY WHEN THE PAGE ASKED FOR IT (`autoGenerate`). This effect used to fire on
   * every mount, and the page remounts this panel on every change of snapshot, so CO-441's new
   * "previous months" list re-billed the model once per click while the manager was still looking
   * for the right month. Three clicks is a Manager's entire daily allowance. `autoGenerate` is
   * false for every route in except pressing Generate, and the empty-state Generate / Retry
   * buttons below are unaffected — a person asking for a narrative was never the defect.
   */
  useEffect(() => {
    if (autoGenerate && !narrative && !readOnly) {
      handleGenerateNarrative();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The manual route out of a failed generation: an EMPTY narrative and the editor open, so what
   * lands in the employee's record is what a person actually wrote.
   */
  const handleWriteManually = () => {
    setNarrative(EMPTY_NARRATIVE);
    setEditedNarrative('');
    setError(null);
    setIsEditing(true);
  };

  const handleGenerateNarrative = async () => {
    setIsGenerating(true);
    setError(null);
    setConfirmingRegenerate(false);

    try {
      /**
       * CO-435 — the REAL material. The old prompt asked the model for "behavioural patterns" while
       * handing it four integers and a list of skill names, so it had nothing to observe patterns in
       * and every narrative came back as a paraphrase of the score card.
       *
       * The fetch is best-effort on purpose: a failed material call falls back to the counts rather
       * than blocking the tab, because a thin narrative beats no narrative.
       */
      let material: NarrativeMaterialView | null = null;
      if (reviewData.snapshotId) {
        try {
          material = await getNarrativeMaterial(reviewData.snapshotId);
        } catch {
          material = null;
        }
      }

      const skillsLines = material?.skillNames?.length
        ? bulletList(material.skillNames, 'No skills recorded')
        : strengthsData.topSkills?.length
          ? strengthsData.topSkills.map(s => `- ${s.skillName} (${s.proficiency}, ${s.endorsementCount} endorsements)`).join('\n')
          : '- No skills data available';

      /**
       * CO-439 — the model is told which MONTH it is writing about, spelled out ("July 2026"), not
       * handed a machine token. `material.period` is the snapshot's own `Period` read back by the
       * server, so it is preferred over the page copy: if the two ever disagree, the server's is the
       * one the numbers in this prompt were computed from.
       */
      const periodLabel = formatPeriodLabel(
        material?.period ?? snapshot?.period,
        i18n.language,
      );

      const goalLines = bulletList(
        (material?.goals ?? []).map((g) => {
          const closed = g.completedDate ? ` (completed ${g.completedDate.slice(0, 10)})` : '';
          const note = g.closureNote ? ` — closing note: "${g.closureNote}"` : '';
          return `${g.title} [${g.category}, ${g.status}]${closed}${note}`;
        }),
        'No goal records for this period',
      );

      /**
       * REFLECTIONS - the employee's OWN account of their month, and the fifth source the snapshot
       * is built from alongside goals, check-ins, recognition and skills.
       *
       * 🔴 THE LABEL IS THE FIX. This block used to read "Manager reflection responses, verbatim:"
       * over a list that is, and always was, the EMPLOYEE'S text (`UserReflection.ResponseText`).
       * The model was therefore told the employee's self-assessment was written by their manager,
       * and dutifully wrote it up as third-party observation - manufactured corroboration, in a
       * document a real person signs. Employee and manager are now named separately, per entry.
       *
       * The richer `reflections` array is preferred; `reflectionResponses` is the fallback for a
       * server build that predates it (ZDD rule 5).
       */
      const reflectionBlock = material?.reflections?.length
        ? `
The employee's own monthly reflections, verbatim (their words, not their manager's):
${material.reflections
  .map((r) => {
    const heading = r.promptTitle ? `${r.promptTitle}: ` : '';
    const reply = r.managerResponse
      ? `\n  Manager's reply to this reflection: "${r.managerResponse}"`
      : '\n  (The manager did not reply to this reflection.)';
    return `- ${heading}"${r.responseText ?? ''}"${reply}`;
  })
  .join('\n')}
`
        : `
The employee's own monthly reflections, verbatim (their words, not their manager's):
${bulletList(material?.reflectionResponses ?? [], 'No reflections with any text this period')}
`;

      /**
       * `withContent` is named explicitly because the card's headline is `submitted`, which counts
       * BLANK submissions too, while the verbatim block below quotes only reflections that have
       * text. Without this line the model could be handed "3 reflections" over prose reading "no
       * reflections with any text", with nothing to reconcile the two.
       */
      const reflectionCountsLine = reflectionsData
        ? `
Reflections:
- Submitted by the employee: ${reflectionsData.submitted} (${reflectionsData.withContent} of them with any text), answered by the manager: ${reflectionsData.withManagerResponse}`
        : '';

      const evidenceBlock = material
        ? `
Goals worked on this period (title, category, status, closing note):
${goalLines}

Recognition this person received, verbatim:
${bulletList(material.recognitionMessages ?? [], 'No recognition messages')}

Check-in notes written about this person, verbatim:
${bulletList(material.checkInNotes ?? [], 'No check-in notes')}

${reflectionBlock}`
        : `
(Detailed material was unavailable for this snapshot. Work from the counts above and say plainly
where the evidence is thin instead of inventing detail.)
`;

      const prompt = `You are an HR AI assistant generating a performance review narrative for ${employee.name}.

Base every statement on the evidence below. Quote or paraphrase what people actually wrote. Do not
invent incidents, and where a section has no material, say the evidence is thin rather than filling
the gap.

Overall Score: ${snapshot?.overallScore ?? 'N/A'}%
Period: ${periodLabel || 'Current'}

Goals Summary:
- Total: ${goalsData.total}, Completed: ${goalsData.completed}, On Track: ${goalsData.onTrack}, At Risk: ${goalsData.atRisk}, Overdue: ${goalsData.overdue}

Check-Ins:
- Total completed: ${checkInsData.total}, With content: ${checkInsData.withContent}, Avg quality: ${checkInsData.avgQuality}%

Recognition:
- Received: ${recognitionData.received}, Given: ${recognitionData.given}
${reflectionCountsLine}

Skills:
${skillsLines}
${evidenceBlock}
Format your response as JSON with these fields:
- summary: A 2-3 paragraph narrative summary grounded in the material above
- goalsAchieved: Array of achieved goal descriptions, naming the goals
- goalsMissed: Array of areas needing improvement, naming the at-risk or overdue goals
- impactHighlights: Array of impact highlights drawn from the recognition messages
- feedbackThemes: Array of themes that recur across the check-in notes and reflections
- behaviorPatterns: Array of behavioural patterns observed across the quoted material
- teamContext: A paragraph about the employee's contribution context`;

      const result = await sendChatMessage({ message: prompt, ephemeral: true });

      if (result.errors?.length) {
        throw new Error(result.errors[0]?.message || t('performanceSnapshot.narrative.generationFailed'));
      }

      /**
       * A FAILED generation is never written into the employee's record.
       *
       * All four failure paths here used to fall back to a hardcoded English paragraph assembled
       * from the score card ("Based on the performance data for X, the overall score is …") and
       * lift it to page state. Since CO-433 that lift AUTOSAVES, so an AI outage silently persisted
       * a fabricated performance narrative into `NarrativeSummary`, where the employee's own
       * read-only view renders it as their review — in English, whatever locale they read the
       * product in. It also satisfied `if (!narrative)` on the next open, so the real narrative was
       * never generated afterwards.
       *
       * A performance narrative nobody wrote must not exist. Surface the failure, leave the field
       * empty, and let the manager retry or write it themselves.
       */
      const message = result.payload?.message;
      if (!message) {
        setError(t('performanceSnapshot.narrative.noResponse'));
        return;
      }

      const jsonMatch = message.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        setError(t('performanceSnapshot.narrative.invalidResponse'));
        return;
      }

      let parsed: NarrativeData;
      try {
        parsed = JSON.parse(jsonMatch[0]) as NarrativeData;
      } catch {
        setError(t('performanceSnapshot.narrative.invalidResponse'));
        return;
      }

      setNarrative(parsed);
      onNarrativeUpdate(parsed);
    } catch (err) {
      console.error('Error generating narrative:', err);
      setError(
        err instanceof Error
          ? err.message
          : t('performanceSnapshot.narrative.unexpectedError'),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveEdit = () => {
    if (!narrative) return;
    const updatedNarrative = {
      ...narrative,
      summary: editedNarrative,
    };
    setNarrative(updatedNarrative);
    onNarrativeUpdate(updatedNarrative);
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    setEditedNarrative(narrative?.summary || '');
    setIsEditing(true);
  };

  if (isGenerating) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Sparkles size={32} className="text-purple-600" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          {t('performanceSnapshot.narrative.generatingTitle')}
        </h3>
        <p className="text-muted-foreground mb-4">
          {t('performanceSnapshot.narrative.generatingDescription', { name: employee.name })}
        </p>
        <div className="flex justify-center space-x-2">
          <div
            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
            style={{ animationDelay: '0ms' }}
          />
          <div
            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
            style={{ animationDelay: '150ms' }}
          />
          <div
            className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      </div>
    );
  }

  if (error && !narrative) {
    return (
      <div className="bg-card border border-red-300 rounded-lg p-8" data-testid="narrative-failed">
        <div className="flex items-start space-x-3">
          <AlertCircle size={24} className="text-red-500" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              {t('performanceSnapshot.narrative.failedTitle')}
            </h3>
            <p className="text-muted-foreground mb-2">{error}</p>
            {/* Say what did NOT happen. Nothing was written, and the manager decides what to do
                about it — the panel does not decide for them by inventing a narrative. */}
            <p className="text-sm text-muted-foreground mb-4 max-w-[70ch]">
              {t('performanceSnapshot.narrative.nothingSaved')}
            </p>
            {!readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleGenerateNarrative}
                  className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors"
                >
                  <RefreshCw size={16} />
                  <span>{t('performanceSnapshot.narrative.retry')}</span>
                </button>
                <button
                  onClick={handleWriteManually}
                  data-testid="narrative-write-manually"
                  className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors"
                >
                  <Edit size={16} />
                  <span>{t('performanceSnapshot.narrative.writeManually')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!narrative) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <FileText size={48} className="mx-auto mb-4 opacity-50" />
        <p className="text-muted-foreground">
          {t('performanceSnapshot.narrative.noNarrative')}
        </p>
        {!readOnly && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={handleGenerateNarrative}
              className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              <Sparkles size={16} />
              <span>{t('performanceSnapshot.narrative.generate')}</span>
            </button>
            <button
              onClick={handleWriteManually}
              data-testid="narrative-write-manually"
              className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              <Edit size={16} />
              <span>{t('performanceSnapshot.narrative.writeManually')}</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-foreground flex items-center space-x-2">
            <FileText size={24} />
            <span>{t('performanceSnapshot.narrative.title')}</span>
          </h3>
          {/* Nothing here can be persisted on a Submitted or Acknowledged snapshot, so nothing
              here is offered on one. */}
          <div className="flex items-center gap-2">
            {readOnly ? null : !isEditing ? (
              <>
                <button
                  onClick={handleStartEdit}
                  className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors"
                >
                  <Edit size={14} />
                  <span>{t('performanceSnapshot.narrative.edit')}</span>
                </button>
                <button
                  onClick={() => setConfirmingRegenerate(true)}
                  disabled={confirmingRegenerate}
                  data-testid="narrative-regenerate"
                  className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={14} />
                  <span>{t('performanceSnapshot.narrative.regenerate')}</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors"
                >
                  <span>{t('performanceSnapshot.narrative.cancel')}</span>
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors"
                >
                  <Save size={14} />
                  <span>{t('performanceSnapshot.narrative.save')}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* A Regenerate that failed over an EXISTING narrative has to say so here — the full-panel
            error state renders only when there is no narrative at all, so this case used to fail
            completely silently while the previous text stayed on screen looking fresh. */}
        {error && (
          <div
            data-testid="narrative-inline-error"
            className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4"
          >
            <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 max-w-[70ch]">
              <p>{error}</p>
              <p className="text-red-700 mt-1">{t('performanceSnapshot.narrative.nothingSaved')}</p>
            </div>
          </div>
        )}

        {/* CO-435 — regenerating throws away whatever the manager wrote, so it asks first. Inline,
            because a modal for a two-word question is heavier than the question. */}
        {confirmingRegenerate && !isEditing && (
          <div
            data-testid="narrative-regenerate-confirm"
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
          >
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900 max-w-[60ch]">
                {t('performanceSnapshot.narrative.regenerateWarning')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setConfirmingRegenerate(false)}
                className="px-3 py-1.5 border border-amber-300 rounded-lg text-sm font-medium text-amber-900 hover:bg-amber-100 transition-colors"
              >
                {t('performanceSnapshot.narrative.cancel')}
              </button>
              <button
                onClick={handleGenerateNarrative}
                data-testid="narrative-regenerate-confirm-action"
                className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
              >
                <RefreshCw size={14} />
                <span>{t('performanceSnapshot.narrative.regenerateConfirm')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Summary */}
        {isEditing ? (
          <textarea
            value={editedNarrative}
            onChange={(e) => setEditedNarrative(e.target.value)}
            className="w-full min-h-[200px] p-4 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            placeholder={t('performanceSnapshot.narrative.editPlaceholder')}
          />
        ) : (
          <div className="prose max-w-none">
            <p className="text-foreground leading-relaxed whitespace-pre-wrap">
              {narrative.summary}
            </p>
          </div>
        )}
      </div>

      {/* Evidence Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Goals Achieved */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Target size={18} className="text-green-500" />
            <span>{t('performanceSnapshot.narrative.goalsAchieved')}</span>
          </h4>
          <ul className="space-y-2">
            {narrative.goalsAchieved?.map((goal, index) => (
              <li key={index} className="flex items-start space-x-2">
                <CheckCircle2
                  size={16}
                  className="text-green-500 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-foreground">{goal}</span>
              </li>
            ))}
          </ul>

          {narrative.goalsMissed?.length > 0 && (
            <>
              <h4 className="font-semibold text-foreground mt-4 mb-3 flex items-center space-x-2">
                <XCircle size={18} className="text-red-500" />
                <span>{t('performanceSnapshot.narrative.goalsMissed')}</span>
              </h4>
              <ul className="space-y-2">
                {narrative.goalsMissed.map((goal, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <AlertCircle
                      size={16}
                      className="text-red-500 mt-0.5 flex-shrink-0"
                    />
                    <span className="text-sm text-foreground">{goal}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Impact Highlights */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Heart size={18} className="text-pink-500" />
            <span>{t('performanceSnapshot.narrative.impactHighlights')}</span>
          </h4>
          <ul className="space-y-2">
            {narrative.impactHighlights?.map((impact, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Star size={16} className="text-pink-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-foreground">{impact}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Feedback Themes */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <MessageCircle size={18} className="text-blue-500" />
            <span>{t('performanceSnapshot.narrative.feedbackThemes')}</span>
          </h4>
          <ul className="space-y-2">
            {narrative.feedbackThemes?.map((theme, index) => (
              <li key={index} className="flex items-start space-x-2">
                <MessageCircle
                  size={16}
                  className="text-blue-500 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-foreground">{theme}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Behavior Patterns */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <TrendingUp size={18} className="text-purple-500" />
            <span>{t('performanceSnapshot.narrative.behaviorPatterns')}</span>
          </h4>
          <ul className="space-y-2">
            {narrative.behaviorPatterns?.map((pattern, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Activity
                  size={16}
                  className="text-purple-500 mt-0.5 flex-shrink-0"
                />
                <span className="text-sm text-foreground">{pattern}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Team Context */}
      {narrative.teamContext && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Users size={18} className="text-amber-500" />
            <span>{t('performanceSnapshot.narrative.teamContext')}</span>
          </h4>
          <p className="text-sm text-foreground leading-relaxed">
            {narrative.teamContext}
          </p>
        </div>
      )}
    </div>
  );
};

export default NarrativeCompilationPanel;
